/* eslint-disable react/no-unused-prop-types */
import React, { useCallback, useRef, useState } from 'react'
import { useMedia } from 'react-use'
import ParseContent from 'components/shared/ParseContent'
import { motion, AnimatePresence } from 'framer-motion'
import Image from 'components/shared/ImageGatsby'

// Third Party
import styled from 'styled-components'

// SVG
import ArrowLeft from 'img/arrow-left.inline.svg'
import ArrowRight from 'img/arrow-right.inline.svg'

interface BannerSliderProps {
  // eslint-disable-next-line
  fields: GatsbyTypes.WpPost_Flexcontent_Flex_Banner
  location?: any
}

const StyledBannerSlider = styled.section`
  height: 730px;

  @media (max-width: 767px) {
    height: 750px;
  }
`

const SlideMotionImageWrapper = styled.div`
  position: relative;
  z-index: 0;
  height: 730px;
`

const SliderOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 1.25rem 1rem 1.25rem 1.25rem;
  pointer-events: none;

  @media (min-width: 768px) {
    padding: 2rem 2.5rem 2rem 3rem;
  }
`

const StyledImage = styled(Image)`
  height: 100%;
  width: 100%;
  max-width: 2000px;
`

const Content = styled(ParseContent)`
  & h1,
  & h2,
  & h3,
  & h4,
  & h5 {
    font-size: 30px;
    font-family: ${(props) => props.theme.font.family.secondary};
    color: ${(props) => props.theme.color.text.secondary};
    font-weight: 700;
    text-transform: uppercase;
    margin-bottom: 2rem !important;
  }

  & p {
    font-size: 18px;
    font-family: ${(props) => props.theme.font.family.main};
    color: ${(props) => props.theme.color.text.main};
    font-weight: 300;
    line-height: 30px;
    margin-bottom: 2rem !important;
  }
`

const SliderWrapper = styled.div`
  pointer-events: auto;
  position: relative;
  width: 100%;
  max-width: 520px;
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
  padding-right: 1.5rem;
  padding-left: calc(15px + 1.5rem);
  background: linear-gradient(
    246deg,
    rgba(255, 255, 255, 0.8) 0.81%,
    rgba(255, 255, 255, 0.5) 52.48%,
    rgba(255, 255, 255, 0.8) 102.01%
  );
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 0px 48px 0px 0px;
  box-shadow:
    0 12px 40px rgba(55, 18, 22, 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.65);

  &::before {
    content: '';
    position: absolute;
    left: 0;
    bottom: -8px;
    width: 157px;
    height: 15px;
    background-color: ${(props) => props.theme.color.face.contrast};
    transform: rotate(-90deg);
    transform-origin: left center;
  }

  @media (min-width: 768px) {
    max-width: 560px;
    padding: 3rem;
    padding-left: calc(15px + 3rem);
  }

  & p {
    letter-spacing: 0.02em;
    color: ${(props) => props.theme.color.text.main};
  }

  @media (max-width: 767px) {
    touch-action: pan-y;
  }
`

const SlideMotionWrapper = styled.div`
  min-height: 200px;
  position: relative;

  @media (max-width: 991px) {
    min-height: 350px;
  }
`

const Slide = styled(motion.div)`
  position: absolute;
  height: 100%;
  width: 100%;
  overflow: auto !important;
`

const NavButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.25rem;
  margin-top: 1rem;
  padding-top: 0.25rem;

  & button {
    background: none;
    border: none;
    padding: 0.25rem;
    cursor: pointer;
    line-height: 0;
  }
`

const SWIPE_THRESHOLD_PX = 50

function BannerSlider({ fields }: BannerSliderProps) {
  const [currentIndex, setCurrentIndex] = useState<number>(0)
  const isMobile = useMedia('(max-width: 767px)')
  const touchStartRef = useRef<{ x: number; y: number } | null>(null)

  const slideCount = fields?.slider?.slide?.length ?? 0

  const handleSliderTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!isMobile || slideCount < 2) return
      touchStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      }
    },
    [isMobile, slideCount]
  )

  const handleSliderTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!isMobile || slideCount < 2 || !touchStartRef.current) return
      const start = touchStartRef.current
      touchStartRef.current = null

      const endX = e.changedTouches[0].clientX
      const endY = e.changedTouches[0].clientY
      const dx = endX - start.x
      const dy = endY - start.y

      if (Math.abs(dx) < Math.abs(dy)) return
      if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return

      setCurrentIndex((i) => {
        if (dx < 0) return (i + 1) % slideCount
        return (i - 1 + slideCount) % slideCount
      })
    },
    [isMobile, slideCount]
  )

  return (
    <StyledBannerSlider className="position-relative mt-lg-5">
      <SlideMotionImageWrapper className="w-100">
        <AnimatePresence>
          {fields?.slider?.slide?.map((slide, index: number) => {
            if (index !== currentIndex) {
              return null
            }

            return (
              <Slide
                className="d-flex justify-content-center"
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <StyledImage
                  image={slide?.image}
                  alt="Lofthus"
                  loading="lazy"
                />
              </Slide>
            )
          })}
        </AnimatePresence>
      </SlideMotionImageWrapper>

      <SliderOverlay className="container">
        <SliderWrapper
          onTouchStart={handleSliderTouchStart}
          onTouchEnd={handleSliderTouchEnd}
        >
          <SlideMotionWrapper>
            <AnimatePresence>
              {fields?.slider?.slide?.map((slide, index: number) => {
                if (index !== currentIndex) {
                  return null
                }

                return (
                  <Slide
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    initial={{ opacity: 0, x: 400, overflow: 'hidden' }}
                    animate={{ opacity: 1, x: 0, overflow: 'hidden' }}
                    exit={{ opacity: 0, x: -400, overflow: 'auto' }}
                  >
                    <Content content={fields?.slider?.title} />
                    <Content content={slide?.description} />
                  </Slide>
                )
              })}
            </AnimatePresence>
          </SlideMotionWrapper>
          <NavButtons>
            <button
              type="button"
              aria-label="Vorige slide"
              onClick={() => {
                setCurrentIndex(
                  (prev) => (prev - 1 + slideCount) % slideCount
                )
              }}
              disabled={slideCount < 2}
            >
              <ArrowLeft />
            </button>
            <button
              type="button"
              aria-label="Volgende slide"
              onClick={() => {
                setCurrentIndex((prev) => (prev + 1) % slideCount)
              }}
              disabled={slideCount < 2}
            >
              <ArrowRight />
            </button>
          </NavButtons>
        </SliderWrapper>
      </SliderOverlay>
    </StyledBannerSlider>
  )
}

export default BannerSlider
